import type { CheckoutCopy } from "./checkout-copy";

export type { CheckoutCopy };

export type ApiVersion = "v1" | "v2";
export type CollectionMode = "business_qr" | "transfer";
/**
 * 微信支付（type=wxpay）的通道策略。
 *
 *   upstream_only —— 只走上游易支付（默认，与历史行为一致）
 *   self_only     —— 只走自挂（自己挂的微信收款码 + 上报）
 *   random        —— 两条都可用时随机选一条
 *   round_robin   —— 两条都可用时轮流（逐单交替）
 *
 * 选中的通道不可用（没配好 / 上游超时）时一律回落到另一条，
 * 两条都不可用才报错。
 */
export type WxChannelStrategy = "upstream_only" | "self_only" | "random" | "round_robin";
export const WX_CHANNEL_STRATEGIES: readonly WxChannelStrategy[] = [
  "upstream_only",
  "self_only",
  "random",
  "round_robin",
];
export type TransferLinkLayer = 1 | 2 | 3;
/** 收银台模板：决定整体样式（布局、卡片质感、留白）。 */
export type CheckoutTemplate = "classic" | "minimal" | "focus";
/** 配色模式：与模板正交，可自由组合。 */
export type CheckoutColorMode = "light" | "dark" | "auto";
/** @deprecated 保留旧名以兼容，等同于 CheckoutTemplate。 */
export type CheckoutTheme = CheckoutTemplate;
export type OrderStatus = "pending" | "expired" | "paid" | "late_paid";

/**
 * 订单来源。
 *   merchant  —— 商户通过易支付接口下单
 *   aggregate —— 访客在聚合支付页（/pay）自助下单
 *   test      —— 后台「支付测试」创建的假订单（不进统计、不占额度）
 */
export type OrderSource = "merchant" | "aggregate" | "test";

export const PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS = 5;
export const PAYMENT_POLL_INTERVAL_MIN_SECONDS = 1;
export const PAYMENT_POLL_INTERVAL_MAX_SECONDS = 60;

/**
 * 删除已支付订单时要求回填的确认短语。
 *
 * 前后端**共用同一份**：以前服务端比对的是平台订单号，文案一改就会两边不一致。
 * 它只是一道「刻意减速」的防呆，不是安全边界 —— 真正的门槛是登录会话。
 */
export const ORDER_DELETE_CONFIRM_PHRASE = "确认删除";

export interface OrderRecord {
  id: string;
  /** 归档时间；非空表示已归档（仅从后台列表收起，记录仍在库中）。 */
  archived_at: string | null;
  trade_no: string;
  pid: string;
  api_version: ApiVersion;
  out_trade_no: string;
  type: "alipay" | "wxpay";
  name: string;
  requested_amount_cents: number;
  payable_amount_cents: number;
  collection_mode: CollectionMode;
  notify_url: string;
  return_url: string | null;
  param: string;
  client_ip: string;
  checkout_token: string;
  status: OrderStatus;
  created_at: string;
  expires_at: string;
  monitor_until: string;
  paid_at: string | null;
  alipay_account_log_id: string | null;
  alipay_order_no: string | null;
  buyer: string;
  channel: "local" | "upstream" | "self_wx";
  /** 订单来源（merchant / aggregate / test）。 */
  order_source: OrderSource;
  upstream_trade_no: string | null;
  upstream_payurl: string | null;
  upstream_amount_cents: number | null;
  upstream_qrcode: string | null;
  /** 分位差额退款状态：none / pending / success / failed / skipped。 */
  refund_status: string;
  refund_amount_cents: number | null;
  refund_error: string | null;
  refunded_at: string | null;
}

export interface DashboardData {
  today_order_count: number;
  today_paid_count: number;
  today_paid_cents: number;
  /** 累计实收：全部已支付（含迟到支付、含已归档）订单的商户原始金额之和。 */
  paid_total_cents: number;
  paid_total_count: number;
  /** 其中本地渠道的金额（钱进你的支付宝）。 */
  paid_local_cents: number;
  /** 其中上游渠道的金额（钱在上游网关侧，由其结算，会先扣掉通道费率）。 */
  paid_upstream_cents: number;
  pending_count: number;
  late_paid_count: number;
  notify_failed_count: number;
  active_monitors: number;
  last_scan: ScanRun | null;
  recent_orders: OrderRecord[];
  configured: boolean;
  collection_mode: CollectionMode;
}

export interface ScanRun {
  id: number;
  started_at: string;
  finished_at: string | null;
  status: "running" | "success" | "error" | "skipped";
  active_orders: number;
  pages: number;
  records: number;
  matched: number;
  error: string | null;
  trace_id: string | null;
}

export interface PublicSettings {
  setup_completed: boolean;
  public_base_url: string;
  collection_mode: CollectionMode;
  transfer_link_layer: TransferLinkLayer;
  payment_poll_interval_seconds: number;
  business_qr_url: string;
  alipay_app_id: string;
  alipay_endpoint: string;
  alipay_configured: boolean;
  v1_enabled: boolean;
  v2_enabled: boolean;
  merchant_pid: string;
  v1_key_masked: string;
  v2_platform_public_key: string;
  v2_merchant_public_key: string;
  allow_private_callbacks: boolean;
  checkout_template: CheckoutTemplate;
  checkout_color_mode: CheckoutColorMode;
  checkout_custom_css: string;
  checkout_copy: CheckoutCopy;
  product_name_rules: Array<{ match: string; display: string }>;
}

export interface CheckoutData {
  trade_no: string;
  out_trade_no: string;
  name: string;
  requested_money: string;
  payable_money: string;
  collection_mode: CollectionMode;
  status: OrderStatus;
  created_at: string;
  expires_at: string;
  monitor_until: string;
  payment_poll_interval_seconds: number;
  payment_uri: string;
  business_qr_url: string;
  return_url: string | null;
  return_target: string | null;
  /** 收银台模板（整体样式）。 */
  checkout_template: CheckoutTemplate;
  /** 配色模式，auto 由前端根据系统偏好解析。 */
  checkout_color_mode: CheckoutColorMode;
  /** 自定义 CSS，在内置样式之后注入。 */
  checkout_custom_css: string;
  /** 收银台文案配置。 */
  checkout_copy: CheckoutCopy;
  /** 当前启用的自定义主题 id，为空时用系统内置默认样式。 */
  active_theme_id: string;
  channel: "local" | "upstream" | "self_wx";
  pay_type: "alipay" | "wxpay";
  upstream_payurl: string;
  /** 上游支付二维码（data URL），优先用于收银台展示。 */
  upstream_qrcode: string;
  /**
   * 自挂微信收款码（data URL）：后台那张固定收款码解码后重画而来。
   * 与上游同理 —— 原图又大又糊，重画后体积小两个数量级、缩放不虚。
   */
  self_wx_qrcode: string;
}

export interface ApiErrorBody {
  error: string;
  message: string;
  details?: unknown;
}
